'use client';
import { type FC, memo, useEffect, useState } from 'react';
import { ElementsSelectorDialog } from '@/app/components/shared/ElementsSelectorDialog';
import { useStore } from '@/app/providers/store';
import type { ElementModality, ElementType, ImagePrompt } from '@/app/types';
export type ElementsDialogState =
  | { type: 'none' }
  | { type: 'elementsSelector'; allowedModalities?: Array<ElementModality> }
  | { type: 'elementView'; element: ElementType }
  | { type: 'elementEdit'; element: ElementType }
  | { type: 'subjectCreate'; initialImagePrompt: ImagePrompt; imageId: string };

interface ElementsDialogProps {
  taskType: 'character2video' | 'reference2image';
  dialogState: ElementsDialogState;
  onDialogStateChange: (state: ElementsDialogState) => void;
  remainingSelections: number;
  maxReferenceCount?: number;
  disabledIds: string[];
  onElementsSelect: (elements: ElementType[]) => void;
  onElementsDelete: (ids: string[]) => void;
  onSubjectSave: (element: ElementType, id?: string | null) => void;
  container?: HTMLDivElement | null;
  mode?: 'dialog' | 'sheet' | 'drawer';
  pendingDeleteIds?: string[];
  setPendingDeleteIds?: (ids: string[]) => void;
}

const ElementsDialog: FC<ElementsDialogProps> = memo(
  ({
    taskType,
    dialogState,
    onDialogStateChange,
    remainingSelections,
    maxReferenceCount,
    disabledIds,
    onElementsSelect,
    onElementsDelete,
    onSubjectSave,
    mode = 'dialog',
    container,
    pendingDeleteIds,
    setPendingDeleteIds,
  }) => {
    const [isElementSelectorOpen, setIsElementSelectorOpen] = useState(false);
    const [creatingSubjectImageId, setCreatingSubjectImageId] = useState<string | null>(null);

    const {
      referenceModalMode,
      referenceModalType,
      openReferenceModalWithData,
      referenceModalUpdateReason,
      closeReferenceModal,
    } = useStore((state) => ({
      referenceModalMode: state.referenceModalMode,
      referenceModalType: state.referenceModalType,
      openReferenceModalWithData: state.openReferenceModalWithData,
      referenceModalUpdateReason: state.referenceModalUpdateReason,
      closeReferenceModal: state.closeReferenceModal,
    }));

    useEffect(() => {
      if (referenceModalUpdateReason?.type === 'update') {
        const id =
          referenceModalMode === 'create' && referenceModalType !== 'share'
            ? creatingSubjectImageId
            : referenceModalUpdateReason.reference.id;
        onSubjectSave(referenceModalUpdateReason.reference, id);
        handleCloseElementDialog();
      } else if (referenceModalUpdateReason?.type === 'delete' || referenceModalUpdateReason?.type === 'cancelSelect') {
        onElementsDelete([referenceModalUpdateReason.reference?.id || '']);
      }
    }, [referenceModalUpdateReason]);

    // 处理dialogState变化
    useEffect(() => {
      if (dialogState.type === 'elementsSelector') {
        setIsElementSelectorOpen(true);
        handleCloseElementDialog();
      } else if (dialogState.type === 'elementView') {
        mode !== 'drawer' && setIsElementSelectorOpen(false);
        openReferenceModalWithData({
          referenceModalOpen: true,
          element: dialogState.element,
          referenceModalMode: 'view',
          referenceModalFrom: taskType,
          referenceModalFromPage: 'others',
        });
      } else if (dialogState.type === 'elementEdit') {
        mode !== 'drawer' && setIsElementSelectorOpen(false);
        openReferenceModalWithData({
          referenceModalOpen: true,
          element: dialogState.element,
          referenceModalMode: 'edit',
          referenceModalFrom: taskType,
        });
      } else if (dialogState.type === 'subjectCreate') {
        mode !== 'drawer' && setIsElementSelectorOpen(false);
        openReferenceModalWithData({
          referenceModalOpen: true,
          element: { modality: 'image', optionalAssets: [dialogState.initialImagePrompt] },
          referenceModalMode: 'create',
          referenceModalType: 'reference',
          referenceModalFrom: taskType,
        });
        setCreatingSubjectImageId(dialogState.imageId);
      } else {
        setIsElementSelectorOpen(false);
        handleCloseElementDialog();
      }
    }, [dialogState, mode]);

    // 处理对话框关闭
    const handleCloseDialog = () => {
      onDialogStateChange({ type: 'none' });
      setIsElementSelectorOpen(false);
    };

    const handleCloseElementDialog = () => {
      closeReferenceModal();
      setCreatingSubjectImageId(null);
    };

    return (
      <>
        {/* 主体选择器 */}
        {isElementSelectorOpen ? (
          <ElementsSelectorDialog
            mode={mode}
            isOpen={isElementSelectorOpen}
            onClose={handleCloseDialog}
            onCreateOpen={() => setIsElementSelectorOpen(false)}
            onSelect={(elements, autoClose = true) => {
              onElementsSelect(elements);
              autoClose && handleCloseDialog();
            }}
            onItemChange={(element) => {
              const id = referenceModalMode === 'create' ? creatingSubjectImageId : element.id;
              onSubjectSave(element, id);
              handleCloseElementDialog();
            }}
            remainingSelections={remainingSelections}
            maxReferenceCount={maxReferenceCount}
            onDelete={onElementsDelete}
            disabledIds={disabledIds}
            container={container}
            allowedModalities={dialogState.type === 'elementsSelector' ? dialogState.allowedModalities : undefined}
            pendingDeleteIds={pendingDeleteIds}
            setPendingDeleteIds={setPendingDeleteIds}
          />
        ) : null}
      </>
    );
  },
);

ElementsDialog.displayName = 'ElementsDialog';

export { ElementsDialog };
